import React, { useState, memo, useEffect } from 'react';
import styled from 'styled-components';

import { Address, WoosmapType } from '@front/common/src/types/commons';
import { EMPTY_ADDRESS } from '@front/common/src/constants/address';
import { buildWoosmapAddress, isValidWoosmapAddress } from '@front/common/src/utils/address';
import { CommonInputProps } from '@front/common/src/components/input/types';
import {
  AutocompleteResponseItem,
  useAddressDetailQuery,
  useAddressQuery,
  useGeolocationQuery,
} from '@front/common/src/queries/woosmap/address-queries';
import { TrackLocationEvent } from '@front/common/src/types/tracking';

import { translate } from '@front/i18n';

import { Suggestion, autocompleteDropdownStyle } from '../../styles/autocomplete';
import Text from '../base-text/variants/text';
import Input from '../input/input';

const t = translate('components.autocomplete_address');

const AutoCompleteDropdown = styled.div`
  ${autocompleteDropdownStyle}
`;

const Container = styled.div`
  position: relative;
  width: 100%;
`;

export type WoosmapAutocompleteLegacyProps = Pick<CommonInputProps, 'error'> & {
  label?: string;
  formattedAddress?: string;
  excludedTypes?: WoosmapType[];
  onEvent?: (event: TrackLocationEvent) => void;
  id?: string;
  name?: string;
  setAddressError: (message?: string) => void;
  apiKey: string;
  setAddressFields: (address: Partial<Omit<Address, 'has_pets'>>) => void;
  onIncompleteAddress?: () => void;
  onBlur?: () => void;
  shouldSortByGeolocation?: boolean;
};

export const WoosmapAutocompleteLegacy = React.forwardRef<HTMLInputElement, WoosmapAutocompleteLegacyProps>(
  (
    {
      label = t('inputs.address.label'),
      id = 'formLocationSelection',
      name = 'addressAutocomplete',
      formattedAddress = '',
      excludedTypes = [],
      onEvent,
      setAddressError,
      setAddressFields,
      error,
      apiKey,
      onIncompleteAddress,
      onBlur,
      shouldSortByGeolocation = false,
    },
    ref,
  ) => {
    const [searchKeyword, setSearchKeyword] = useState<string>('');
    const [isTouchedOutside, setIsTouchedOutside] = useState(false);
    const geolocation = useGeolocationQuery({ apiKey, enabled: shouldSortByGeolocation });
    const { data: suggestions, isSuccess: isAddressSuccess } = useAddressQuery({
      keyword: searchKeyword,
      location: geolocation?.latitude ? `${geolocation.latitude},${geolocation.longitude}` : undefined,
      apiKey,
    });

    const [selectedSuggestion, setSelectedSuggestion] = useState<AutocompleteResponseItem>();

    // This query is triggered only when we have a selectedSuggestion.public_id
    // So this means, it will be enabled only when a suggestion on the dropdown is clicked
    const { addressDetail } = useAddressDetailQuery({ publicId: selectedSuggestion?.public_id, apiKey });

    useEffect(() => {
      if (addressDetail) {
        if (isValidWoosmapAddress(addressDetail)) {
          setAddressFields(buildWoosmapAddress(addressDetail));
          setSearchKeyword('');
          setAddressError(undefined);
        } else {
          setAddressError(t('.errors.address'));
          onIncompleteAddress?.();
          if (onEvent) onEvent('UnableToGeolocalize');
        }
      }
      // We only want this effect to be triggered when the addressDetail change
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [addressDetail]);

    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      setAddressFields(EMPTY_ADDRESS);
      setSelectedSuggestion(undefined);
      setIsTouchedOutside(false);
      setSearchKeyword(e.target.value);
    };

    const handleSelect = (suggestion: AutocompleteResponseItem) => () => {
      setIsTouchedOutside(true);
      setSelectedSuggestion(suggestion);
    };

    const filteredSuggestions = excludedTypes.length
      ? suggestions?.filter((suggestion) => !suggestion.types.some((type) => excludedTypes.includes(type)))
      : suggestions;

    return (
      <Container>
        <Input
          ref={ref}
          id={id}
          name={name}
          autoComplete='off'
          label={label}
          error={error}
          onChange={handleChange}
          onBlur={onBlur}
          value={searchKeyword.length > 0 ? searchKeyword : formattedAddress}
        />
        {filteredSuggestions && filteredSuggestions.length > 0 && !isTouchedOutside && isAddressSuccess && (
          <AutoCompleteDropdown className='autocomplete-dropdown-container' role='listbox'>
            {filteredSuggestions.map((suggestion) => (
              <Suggestion key={suggestion.public_id} onClick={handleSelect(suggestion)} role='option'>
                <Text>{suggestion.description}</Text>
              </Suggestion>
            ))}
          </AutoCompleteDropdown>
        )}
      </Container>
    );
  },
);

export default memo(WoosmapAutocompleteLegacy);
